import clsx from 'clsx'
import { Link } from 'wouter'
import { useCallback } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useApolloClient } from '@apollo/client/react'
import { useLingui, Trans } from '@lingui/react/macro'

import {
  FlowConnectionIcon,
  Invoice01Icon,
  PlusSignIcon,
  Settings01Icon,
  UserGroupIcon
} from '@hugeicons/core-solid-rounded'

import { useAuth } from '@/features/auth'
import { logoPreview } from '@/features/files'
import useOrganization from '../hooks/useOrganization'
import { useBranding } from '@/features/branding'
import useOrganizations from '../hooks/useOrganizations'
import { Suspense } from '@/components/ui/Suspense'
import { popoverContainer } from '@/utils/popovers'

interface Props {
  type: 'image_only' | 'row' | 'stacked'
  size?: 'small' | 'normal'
  color?: 'text' | 'white'
  name: string
  image: string
}

function OrganizationPreview({
  type,
  color = 'text',
  name,
  image,
  size = 'normal'
}: Props) {
  return (
    <div
      className={clsx(
        'flex flex-row p-2 gap-2 items-center text-heading3',
        type === 'stacked' && 'flex-col',
        color === 'text' && 'text-text',
        color === 'white' && 'text-white',
        size === 'normal' && 'text-heading3',
        size === 'small' && 'text-regular'
      )}
    >
      <img
        className={clsx(
          'rounded-full shrink-0',
          size === 'normal' && 'size-6',
          size === 'small' && 'size-4'
        )}
        src={image}
      />
      {type !== 'image_only' && <h3 className="text-left">{name}</h3>}
    </div>
  )
}

function SwitchOrganizationList({ itemClasses }: { itemClasses: string }) {
  const client = useApolloClient()
  const { switchOrg } = useAuth()
  const { organization } = useOrganization()
  const organizations = useOrganizations()

  const onSelectOrg = useCallback(
    (slug: string) => {
      switchOrg(slug).then(session => {
        if (session) {
          // We don't support multi tenancy caching currently, so let's just dump it all
          client.resetStore()

          window.location.reload()
        }
      })
    },
    [client, switchOrg]
  )

  return (
    <div className="flex flex-col gap-2">
      <DropdownMenu.Label asChild>
        <span className="text-text-light text-regular font-bold pt-5">
          <Trans>Switch Hotel</Trans>
        </span>
      </DropdownMenu.Label>
      <DropdownMenu.Group>
        {organizations.map(function ({ slug, name }) {
          // We don't want to show the currently active organization
          if (slug === organization.slug) return

          return (
            <DropdownMenu.Item
              className={itemClasses}
              onClick={() => onSelectOrg(slug)}
              key={slug}
            >
              {name}
            </DropdownMenu.Item>
          )
        })}
        <DropdownMenu.Item asChild>
          <Link to="/create-organization" className={itemClasses}>
            <HugeiconsIcon
              icon={PlusSignIcon}
              className="size-4"
              strokeWidth={3}
            />
            <Trans>New Hotel</Trans>
          </Link>
        </DropdownMenu.Item>
      </DropdownMenu.Group>
    </div>
  )
}

function Content() {
  const itemClasses = clsx(
    'flex cursor-pointer flex-row items-center gap-2.5 px-4 py-2 rounded-small text-text text-regular outline-hidden focus:bg-primary-bright'
  )

  return (
    <div className="flex flex-col p-2 gap-1">
      <DropdownMenu.Item asChild>
        <Link to="/organization-members" className={itemClasses}>
          <HugeiconsIcon icon={UserGroupIcon} className="size-4" />
          <span className="py-2">
            <Trans>Members</Trans>
          </span>
        </Link>
      </DropdownMenu.Item>
      <DropdownMenu.Item asChild>
        <Link to="/organization-billing" className={itemClasses}>
          <HugeiconsIcon icon={Invoice01Icon} className="size-4" />
          <span className="py-2">
            <Trans>Billing</Trans>
          </span>
        </Link>
      </DropdownMenu.Item>
      <DropdownMenu.Item asChild>
        <Link to="/integrations" className={itemClasses}>
          <HugeiconsIcon icon={FlowConnectionIcon} className="size-4" />
          <span className="py-2">
            <Trans>Integrations</Trans>
          </span>
        </Link>
      </DropdownMenu.Item>
      <DropdownMenu.Item asChild>
        <Link to="/organization-settings" className={itemClasses}>
          <HugeiconsIcon icon={Settings01Icon} className="size-4" />
          <span className="py-2">
            <Trans>Settings</Trans>
          </span>
        </Link>
      </DropdownMenu.Item>
      <Suspense>
        <SwitchOrganizationList itemClasses={itemClasses} />
      </Suspense>
    </div>
  )
}

export function OrganizationButton(props: Props) {
  const { t } = useLingui()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t`Open Organization Popover`}
        className="rounded-l"
      >
        <OrganizationPreview {...props} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal container={popoverContainer}>
        <DropdownMenu.Content
          className="min-w-[350px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-3 rounded-small bg-white p-3 shadow-md outline-hidden z-popover"
          sideOffset={5}
        >
          <Content />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

type CurrentProps = Omit<Props, 'name' | 'image'>

export function CurrentOrganizationButton(props: CurrentProps) {
  const { organization } = useOrganization()
  const { branding } = useBranding()

  return (
    <OrganizationButton
      name={organization.name}
      image={logoPreview(branding.logo, branding.colors.navBackground)}
      {...props}
    />
  )
}
